import { getAuth } from 'firebase/auth';
import { JeopardyClue } from './jeopardy-types';
import { resolveBackendBaseUrl } from 'utils/backend-api';

export type JeopardyDifficulty = JeopardyClue['difficulty'];

export interface GenerateJeopardyCluesPayload {
    category: string;
    difficulty: JeopardyDifficulty;
    count: number;
}

interface GenerateJeopardyCluesResponse {
    clues: Array<{
        points: number;
        question: string;
        answer: string;
        difficulty: JeopardyDifficulty;
    }>;
}

export class JeopardyGenerateApiError extends Error {
    statusCode?: number;

    constructor(message: string, statusCode?: number) {
        super(message);
        this.name = 'JeopardyGenerateApiError';
        this.statusCode = statusCode;
    }
}

function normalizeErrorPayload(payload: unknown, fallbackMessage: string): string {
    if (!payload || typeof payload !== 'object') {
        return fallbackMessage;
    }

    const typedPayload = payload as Record<string, unknown>;
    const message = typedPayload.message;

    if (Array.isArray(message)) {
        return message
            .map(item => (typeof item === 'string' ? item : 'Validation error'))
            .join('; ');
    }

    if (typeof message === 'string' && message.trim().length > 0) {
        return message;
    }

    if (typeof typedPayload.error === 'string' && typedPayload.error.trim().length > 0) {
        return typedPayload.error;
    }

    return fallbackMessage;
}

export async function generateJeopardyClues(
    payload: GenerateJeopardyCluesPayload,
): Promise<GenerateJeopardyCluesResponse> {
    const currentUser = getAuth().currentUser;

    if (!currentUser) {
        throw new JeopardyGenerateApiError(
            'You must be signed in to use AI clue generation.',
            401,
        );
    }

    const token = await currentUser.getIdToken();
    const baseUrl = resolveBackendBaseUrl();
    const endpoint = `${baseUrl}/generate/jeopardy-clues`;

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    const rawText = await response.text();
    let parsedPayload: unknown = null;

    if (rawText) {
        try {
            parsedPayload = JSON.parse(rawText);
        } catch {
            parsedPayload = {
                error: rawText,
            };
        }
    }

    if (!response.ok) {
        const message = normalizeErrorPayload(
            parsedPayload,
            'AI clue generation failed.',
        );

        throw new JeopardyGenerateApiError(message, response.status);
    }

    if (!parsedPayload || typeof parsedPayload !== 'object') {
        throw new JeopardyGenerateApiError('Invalid AI response payload.');
    }

    const clues = (parsedPayload as Record<string, unknown>).clues;
    if (!Array.isArray(clues)) {
        throw new JeopardyGenerateApiError('Invalid AI response shape.');
    }

    return parsedPayload as GenerateJeopardyCluesResponse;
}
